import { useCallback } from 'react';
import { useI18nStrings, type I18nSurface } from '@/hooks/use-i18n-strings';
import { useLocaleStore } from './store';

/**
 * Translation hook for the admin UI.
 *
 * Resolution order: active locale → English → the key itself. If the catalogue
 * hasn't loaded yet, we fall back to the key so nothing renders as empty.
 *
 * Usage:
 *   const t = useT('admin_ui');
 *   return <h1>{t('nav.dashboard')}</h1>;
 */
export function useT(surface: I18nSurface = 'admin_ui') {
  const locale = useLocaleStore((s) => s.locale);
  const { data: active } = useI18nStrings(surface, locale);
  const { data: fallback } = useI18nStrings(surface, 'en');

  return useCallback(
    (key: string, bakedEnglish?: string): string => {
      const fromActive = active?.[key];
      if (fromActive !== undefined) return fromActive;
      const fromFallback = fallback?.[key];
      if (fromFallback !== undefined) return fromFallback;
      return bakedEnglish ?? key;
    },
    [active, fallback],
  );
}
